import {useSendPasswordlessLoginMutation} from "@components/Booking/EnterEmail/graphql/__generated__/send-passwordless-login.type";
import {GraphQLError} from "graphql";
import {useCallback, useMemo, useState} from "react";

import {EventType} from "../../../utils/analytics/constants";
import {queueEvent} from "../../../utils/analytics/eventQueue";

type Params = {
  flowId: string;
  redirectUrl: string;
  isResend?: boolean;
};

type EmailParams = {
  email: string;
};

type EmailOrSmsParams = {
  patientId: string;
};

/**
 * If `email` or `patientId` is defined, `sendEmail` will always be defined.
 * `patientId` is required for `sendSms`
 */
export function useSendLoginToken(options: Params & (EmailParams | EmailOrSmsParams)): {
  success: boolean;
  sendEmail: () => Promise<void>;
  sendSms: (() => Promise<void>) | undefined;
};

export function useSendLoginToken({
  redirectUrl,
  isResend,
  flowId,
  ...rest
}: Params & (EmailParams | EmailOrSmsParams)) {
  const email = "email" in rest ? rest.email : undefined;
  const patientId = "patientId" in rest ? rest.patientId : undefined;

  const [sendPasswordlessLoginMutation] = useSendPasswordlessLoginMutation();
  const [success, setSuccess] = useState(false);

  const sendLinkSentEvent = useCallback(
    (mode: "email" | "sms", errors: readonly GraphQLError[] | undefined) => {
      queueEvent({
        typeId: EventType.PasswordlessLoginLinkResent,
        extraData: {
          flowId,
          patientId,
          mode,
          success: !errors || errors.length === 0,
          redirectUrl,
        },
      });
    },
    [flowId, patientId, redirectUrl],
  );

  /**
   * Requires either email or patientId.
   */
  const sendEmail = useMemo(() => {
    if (!patientId && !email) {
      // Typescript should prevent this from happening, but just in case...
      throw Error("Either `patientId` or `email` must be defined.");
    }
    return async () => {
      const patientOrEmail = patientId
        ? {
            patient: {
              overEmail: true,
              overSMS: false,
              patientId: patientId,
            },
          }
        : {
            email,
          };

      const {errors} = await sendPasswordlessLoginMutation({
        variables: {
          input: {
            maxTtlDesired: "PT168H",
            redirectUrl,
            flowId,
            isResend,
            ...patientOrEmail,
          },
        },
      });
      sendLinkSentEvent("email", errors);
      setSuccess(true);
    };
  }, [
    email,
    flowId,
    isResend,
    patientId,
    redirectUrl,
    sendLinkSentEvent,
    sendPasswordlessLoginMutation,
  ]);

  /**
   * Requries patientId
   */
  const sendSms = useMemo(() => {
    if (!patientId) return undefined;
    return async () => {
      const {errors} = await sendPasswordlessLoginMutation({
        variables: {
          input: {
            maxTtlDesired: "PT168H",
            redirectUrl,
            flowId,
            isResend,
            patient: {
              patientId,
              overEmail: false,
              overSMS: true,
            },
          },
        },
      });
      sendLinkSentEvent("sms", errors);
      setSuccess(true);
    };
  }, [flowId, isResend, patientId, redirectUrl, sendLinkSentEvent, sendPasswordlessLoginMutation]);

  return {success, sendEmail, sendSms};
}
